/**
 * Verdict-queue + routing contracts — RO-2-09e / #44418.
 *
 * Ported from the remaining loop-mutation actions in
 * `excellent/src/optimization/actions.ts` that the Workflow Studio contract
 * (RO-3-02b) deliberately left out because they live on `/verdicts` and
 * `/settings/agents/verdicts`, not `/agents/workflows/[slug]`:
 *
 *   submitVerdictAction          → POST /api/verdicts/runs/:id/submit
 *   annotateVerdictAction        → POST /api/verdicts/annotate
 *   setVerdictRoutingAction      → POST /api/verdicts/routing
 *   approvePromotionFromQueue    → POST /api/verdicts/promotions/approve
 *   rejectPromotionFromQueue     → POST /api/verdicts/promotions/reject
 *
 * `approvePromotionAction` / `rejectPromotionAction` (slug-scoped studio path)
 * already live on `workflowDetail.approvePromotion` / `.rejectPromotion`. The
 * FromQueue twins shell the same `optimize` CLI with only a promotion id; they
 * need a non-slug path because the review inbox has no loop slug in hand.
 *
 * Live Next call sites stay on their server actions (`ccb89192`).
 *
 * Dropped `revalidatePath` targets (L5 handoff):
 *   submit / annotate / FromQueue → /verdicts
 *   setVerdictRouting             → /settings/agents/verdicts, /verdicts
 */

import { z } from "zod";
import type { Operation } from "../types";

const optimizeCliResult = z.custom<{ ok: boolean; data?: unknown; error?: string }>();

// ── Submit human verdict on a worker run ────────────────────────────────────

const submitParams = z.object({ id: z.string().min(1) });
const submitBody = z.object({
  verdict: z.enum(["accept", "reject", "edit"]),
  note: z.string().optional(),
  editedOutput: z.string().optional(),
  /** §5.5 presented_as — override-as-diff UI passes `diff`. */
  presented_as: z.enum(["diff", "raw"]).optional(),
  /** §5.5 wall_ms — reviewer time when measured. */
  wall_ms: z.number().int().nonnegative().optional(),
  /** Epoch ms presented; seam subtracts when wall_ms omitted. */
  presented_at_ms: z.number().int().positive().optional(),
});
export type VerdictSubmitBody = z.infer<typeof submitBody>;

const submitOp: Operation<
  { id: string },
  void,
  VerdictSubmitBody,
  { ok: true } | { ok: false; error: string }
> = {
  method: "POST",
  path: ({ id }) => `/api/verdicts/runs/${encodeURIComponent(id)}/submit`,
  params: submitParams,
  body: submitBody,
  response: z.custom<{ ok: true } | { ok: false; error: string }>(),
};

// ── Annotate without deciding ───────────────────────────────────────────────

const annotateBody = z.object({
  kind: z.enum(["run", "promotion"]),
  id: z.number().int().positive(),
  note: z.string(),
});
export type VerdictAnnotateBody = z.infer<typeof annotateBody>;

const annotateOp: Operation<void, void, VerdictAnnotateBody, { ok: boolean; error?: string }> = {
  method: "POST",
  path: () => "/api/verdicts/annotate",
  body: annotateBody,
  response: z.custom<{ ok: boolean; error?: string }>(),
};

// ── Department → approver routing table ─────────────────────────────────────

const routingBody = z.object({
  /** department id → actor external_id, or null/"" for unassigned. */
  routing: z.record(z.string().nullable()),
});
export type VerdictRoutingBody = z.infer<typeof routingBody>;

const setRoutingOp: Operation<void, void, VerdictRoutingBody, { ok: true }> = {
  method: "POST",
  path: () => "/api/verdicts/routing",
  body: routingBody,
  response: z.object({ ok: z.literal(true) }),
};

// ── Promotion approve/reject from the verdict queue (no loop slug) ──────────

const promotionBody = z.object({ promotionId: z.number().int().positive() });
export type VerdictPromotionBody = z.infer<typeof promotionBody>;

const approvePromotionOp: Operation<void, void, VerdictPromotionBody, z.infer<typeof optimizeCliResult>> = {
  method: "POST",
  path: () => "/api/verdicts/promotions/approve",
  body: promotionBody,
  response: optimizeCliResult,
};

const rejectPromotionOp: Operation<void, void, VerdictPromotionBody, z.infer<typeof optimizeCliResult>> = {
  method: "POST",
  path: () => "/api/verdicts/promotions/reject",
  body: promotionBody,
  response: optimizeCliResult,
};

export const verdicts = {
  submit: submitOp,
  annotate: annotateOp,
  setRouting: setRoutingOp,
  approvePromotion: approvePromotionOp,
  rejectPromotion: rejectPromotionOp,
};
